import 'regenerator-runtime/runtime';
import 'url-search-params-polyfill';
import React from 'react';
import axios from 'axios';
import ReactDOM from 'react-dom';
import { wixAxiosConfig } from '@wix/wix-axios-config';
import {
  i18nInboxCommonService,
  setChatInstallationDate,
} from '@wix/inbox-common';
import { i18n as inboxCommonI18n } from '@wix/inbox-common/dist/src/translations/i18n';
import i18n from '../../i18n';
import wix from '../wix';
import { fedopsLogger, LoadingPhase } from '../fedopsLogger';
import { loadingScheduler } from '../loading-scheduler';
import { wrapWithEnvironment } from './wrapper';
import { extractSiteData } from '../site-data';
import { siteDataFromSiteJson } from '../site-json';
import { loadChat } from './chat-loader';
import * as biLogger from '../biLogger';
import { initSentry } from '../sentry-integration';
import { getLinguistParams } from '../linguist-integration';

fedopsLogger.init({ appName: 'chat-widget' });

const staticsBaseUrl = window.__STATICS_BASE_URL__;

const installationDate = window.__INSTALLATION_DATE__;
const isInTemplate = wix.isInTemplate();

setChatInstallationDate(isInTemplate ? 0 : installationDate);

const {
  publicVisibility,
  editorVisibility,
  chatOnboarded,
  businessChatEnabled,
  socialChatEnabled,
} = window.__SETTINGS__;
const experiments = window.__EXPERIMENTS__;
const chatInitResult = window.__CHAT_INIT_RESULT__;
const isLcfMandatory =
  window.__IS_LCF_MANDATORY__ === undefined
    ? { isMandatory: false, isContact: false }
    : window.__IS_LCF_MANDATORY__;
const siteJson = window.__SITE_JSON__;
const widgetMode = window.__WIDGET_MODE__;
const unpkgUrl = window.__UNPKG_URL__;
const locale = window.__SITE_LANGUAGE__;
const localizedUserText = window.__LOCALIZED_USER_TEXT__.header;
const viewMode = wix.getViewMode();
const deviceType = wix.getDeviceType();
const visibilitySettings = {
  publicVisibility,
  editorVisibility,
  chatOnboarded,
};

const businessLocale = locale;

const urlParams = new URLSearchParams(window.location.search);
const host = decodeURIComponent(urlParams.get('host') || 'Wix');
const disableAutoMessages = urlParams.has('disableAutoMessages');
const {
  lang: userSelectedLanguage,
  dateNumberFormat,
  isPrimaryLanguage,
} = getLinguistParams();

const isBranded = chatInitResult
  ? chatInitResult.isBranded
  : 'No Chat Init Result';
initSentry(staticsBaseUrl, {
  userId: wix.getUid() || wix.getAid(),
  widgetMode,
  viewMode,
  deviceType,
  locale: userSelectedLanguage,
  isBranded,
});

wixAxiosConfig(axios, {
  baseURL: '/',
  lang: userSelectedLanguage,
  locale: dateNumberFormat,
  isPrimaryLanguage,
  siteInstance: wix.getInstance(),
});

// initialize via both main-entry and specific file within dist/src. TODO: refactor
i18nInboxCommonService.i18n({
  locale: userSelectedLanguage,
  fallbackLang: businessLocale,
});
inboxCommonI18n({
  locale: userSelectedLanguage,
  fallbackLang: businessLocale,
});
const i18nData = i18n({
  siteLanguage: userSelectedLanguage,
  fallbackLanguage: businessLocale,
  baseUrl: staticsBaseUrl,
});

biLogger.initLogger({
  instanceId: wix.getInstanceId(),
  biToken: wix.getBiToken(),
  visitorId: wix.getAid(),
  viewMode,
  isSocial: socialChatEnabled,
  isBusiness: businessChatEnabled,
});

const siteData = siteDataFromSiteJson(siteJson);
const initialDataFromSiteData = extractSiteData(siteData, experiments);

const initialState = {
  visibilitySettings,
  viewMode,
  locale: userSelectedLanguage,
  deviceType,
  chatInitResult,
  businessChatEnabled,
  socialChatEnabled,
  isLcfMandatory,
  widgetMode,
  disableAutoMessages,
  localizedUserText,
  isPrimaryLanguage,
  chatOnPage: wix.getViewMode() === 'standalone',
  host,
  ...initialDataFromSiteData,
};

fedopsLogger.appLoadingPhaseStart(LoadingPhase.Bootstrap);

export const init = (component: React.ComponentClass) => {
  const environment = {
    i18n: i18nData,
    experiments,
    initialState,
  };

  void loadingScheduler.onReadyToPaint().then(() => {
    fedopsLogger.appLoadingPhaseStart(LoadingPhase.Paint);

    ReactDOM.render(
      wrapWithEnvironment(environment)(React.createElement(component)),
      document.getElementById('root'),
    );

    loadChat({ widgetMode, unpkgUrl });
  });

  loadingScheduler.triggerPaint();
};
